import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { IHttpResponse } from 'angular';
import { from, Observer, of, throwError } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Auth } from 'Src/ng2/shared/auth/auth.service';
import { API_AUTH_FAILURE_MESSAGE } from 'Src/ng2/shared/constants/auth/auth.constant';
import { ModalsService } from 'Src/ng2/shared/modals/modals.service';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { LoadCurrentUserSuccess, LoadCurrentUserFail } from '../../store/actions/current-user-actions';
import { getCurrentUser, getCurrentUserLoadedStatus } from '../../store/selectors/current-user-selectors';
import { IRouteConfigsOpts } from './../routes.config';
import { ErrorModalComponent } from 'Src/ng2/shared/modals/error/error-modal.component';

declare const window: Window;

@Injectable()
export class RouteGuards implements CanActivate {
  constructor(
    @Inject('Rollbar') private rollbar,
    @Inject('User') private user,
    private auth: Auth,
    private imUser: ImUser,
    private modalsService: ModalsService,
    private store: Store<any>,
  ) {}

  canActivate(snapshot: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // cast the snapshot.routeConfigs because the route config interface contains additional props to accomodate our hybrid state
    const routeConfig = snapshot.routeConfig as IRouteConfigsOpts;
    const { authenticationRequired } = routeConfig;
    return authenticationRequired ? this.checkAuthAndRole$(routeConfig, state.url) : this.checkRedirect$(routeConfig);
  }

  private checkAuthAndRole$(routeConfig, url): Observable<boolean> {
    const authenticated = this.auth.isAuthenticated();
    return authenticated ? this.canAccessRoute$(routeConfig, url) : this.redirectToLogin$({ lastKnownUrl: url });
  }

  private canAccessRoute$(routeConfig: IRouteConfigsOpts, url: string): Observable<boolean> {
    return this.getCurrentUser$().pipe(
      catchError(err => {
        this.handleApiError({ err, lastKnownUrl: url });
        return of(null);
      }),
      filter(user => user),
      switchMap(user => this.isRoleAuthorized$(user, routeConfig, url)),
      switchMap(isRouteAccessible =>
        isRouteAccessible ? this.checkRedirect$(routeConfig) : this.redirectToHome$({ noAccessUrl: url }),
      ),
    );
  }

  private isRoleAuthorized$(user: IUser, routeConfig: IRouteConfigsOpts, url: string): Observable<boolean> {
    return Observable.create((observer: Observer<any>) => {
      const { rolePermissions: rolesThatCanAccessRoute } = routeConfig;
      const authorized = this.imUser.isRoleAuthorized({
        user,
        rolesThatCanAccessRoute,
        stateOrUrl: url,
        partnerType: 'shelter',
      });
      authorized ? observer.next(true) : observer.next(false);
      observer.complete();
    });
  }

  private getCurrentUser$(): Observable<IUser> {
    return this.store.select(getCurrentUserLoadedStatus).pipe(
      switchMap(isLoaded => {
        return isLoaded ? this.store.select(getCurrentUser).pipe(take(1)) : this.loadCurrentUser$();
      }),
      // manage subscription since we are streaming from the store
      take(1),
    );
  }

  private loadCurrentUser$(): Observable<IUser> {
    return this.fetchCurrentUser$().pipe(
      tap(currentUser => {
        this.imUser.setCurrentUserSession(currentUser);
        this.imUser.setRollbarConfig(currentUser);
      }),
      tap(currentUser => this.store.dispatch(new LoadCurrentUserSuccess(currentUser))),
    );
  }

  private fetchCurrentUser$(): Observable<IUser> {
    const currentUserPromise: Promise<IUser> = this.auth.getCurrentUserId().then(currentUserId => {
      const $user = new this.user({ _id: currentUserId });
      const currentUser = $user.$get();
      return currentUser;
    });
    return from(currentUserPromise);
  }

  private checkRedirect$(routeConfig: IRouteConfigsOpts): Observable<boolean> {
    const { redirectTo } = routeConfig;
    return Observable.create((observer: Observer<any>) => {
      if (redirectTo) {
        this.redirect(redirectTo);
        observer.next(true), observer.complete();
      }
      observer.next(false), observer.complete();
    }).pipe(map(redirectInProgress => !redirectInProgress));
  }

  private redirectToLogin$(logoutOpts?: { errorMessage?: string; lastKnownUrl?: string }): Observable<boolean> {
    return Observable.create((observer: Observer<any>) => {
      observer.next(false), observer.complete();
    }).pipe(
      // pass lastKnownUrl down to auth.logout so that once users are authenticated they can be routed back to previous url
      tap(() => this.auth.logout(logoutOpts)),
    );
  }

  private redirectToHome$(context: { noAccessUrl: string }): Observable<boolean> {
    const title = 'Unauthorized Access';
    const message = `You cannot access "${
      context.noAccessUrl
    }" because your role does not allow it. Please contact your administrator to request a role change.`;
    return this.modalsService
      .openErrorModal({ title, message })
      .afterClosed()
      .pipe(
        switchMap(() => of(false)),
        tap(() => this.redirect('/home')),
      );
  }

  private redirect(url: string): void {
    window.location.pathname = url;
  }

  // Mimic api err handler from main.route.ts for top level `main` state
  // Search `AuthenticationRequiredError` to spot the original logic - jchu
  private handleApiError(context: { err: IHttpResponse<{ message: string }> | Error; lastKnownUrl: string }): void {
    const { err, lastKnownUrl } = context;
    const httpResErr = err as IHttpResponse<{ message: string }>;
    const hasHttpAuthErr =
      httpResErr.xhrStatus === 'error' || (httpResErr.data && httpResErr.data.message === API_AUTH_FAILURE_MESSAGE);
    const regErr = err as Error;
    const hasRegAuthErr = regErr.message === API_AUTH_FAILURE_MESSAGE;
    let loginErrToDisplay;
    if (hasHttpAuthErr || hasRegAuthErr) {
      loginErrToDisplay = 'Unauthorized';
    } else {
      loginErrToDisplay = ((httpResErr.data && httpResErr.data.message) || regErr.message).slice(0, 50);
    }
    this.rollbar.debug('RouteGuards#handleApiError', err);
    this.auth.logout({ errorMessage: loginErrToDisplay, lastKnownUrl });
  }
}
